import * as React from "react";
import Box from "@mui/material/Box";
import Popover from "@mui/material/Popover";
import List from "@mui/material/List";
import MenuItem from "@mui/material/MenuItem";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";

// Company selector for company-scoped pages, styled to match StoreName
// (plain text + caret, popover list). Selecting a company switches the
// home store to a store in that company (keeping the current store when it
// already belongs), so every page keyed off homeStore/homeCompany follows.
export default function CompanyName(props) {
  const [open, setOpen] = React.useState(false);
  const anchorRef = React.useRef(null);

  const companyOptions = React.useMemo(() => {
    const unique = [];
    (props.storeNames || []).forEach((s) => {
      if (s.company && !unique.some((c) => c.id === s.company)) {
        unique.push({
          id: s.company,
          name: s.company_info?.company_name || s.company_info?.name || "",
        });
      }
    });
    // Optional feature filter (array of company ids): callers pass the
    // companies where their feature is enabled somewhere. Only the LIST is
    // filtered; anchor-store selection below still sees every membership
    // so a manager membership in the target company is preferred even if
    // that particular store has the feature off.
    const eligible = props.eligibleCompanyIds
      ? new Set(props.eligibleCompanyIds)
      : null;
    return unique
      .filter((c) => !eligible || eligible.has(c.id) || c.id === props.homeCompany)
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [props.storeNames, props.eligibleCompanyIds, props.homeCompany]);

  const currentName =
    companyOptions.find((c) => c.id === props.homeCompany)?.name || "";

  const handleSelect = (companyId) => {
    setOpen(false);
    if (companyId === props.homeCompany) return;
    const companyStores = (props.storeNames || []).filter(
      (s) => s.company === companyId
    );
    const storeIds = new Set(companyStores.map((s) => s.id));
    // Prefer a manager/admin membership in the new company so role-scoped
    // pages (Tickets, Contests) show as much as possible; roles can differ
    // across companies.
    const memberships = (props.storeOptions || []).filter((m) =>
      storeIds.has(m.store)
    );
    const managerMembership = memberships.find((m) =>
      ["company_manager", "company_admin", "rightwork_internal"].includes(
        m.permission_type
      )
    );
    const targetId =
      (companyStores.find((s) => s.id === props.homeStore) || {}).id ??
      managerMembership?.store ??
      memberships[0]?.store ??
      companyStores[0]?.id;
    if (targetId != null && props.change_home_store) {
      props.change_home_store(targetId);
    }
  };

  if (companyOptions.length <= 1) {
    return (
      <Box>
        <p className="text-slate-900 truncate max-w-[220px]">{currentName}</p>
      </Box>
    );
  }

  return (
    <Box>
      <div
        ref={anchorRef}
        className="flex items-center gap-x-0.5 cursor-pointer"
        onClick={() => setOpen(true)}
      >
        <p className="text-slate-900 truncate max-w-[220px]">{currentName}</p>
        <div className="text-slate-500 flex-shrink-0">
          {open ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}
        </div>
      </div>
      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
      >
        <Box py={1} width="260px">
          <List dense>
            {companyOptions.map((c) => (
              <MenuItem
                key={c.id}
                onClick={() => handleSelect(c.id)}
                selected={props.homeCompany === c.id}
              >
                {c.name}
              </MenuItem>
            ))}
          </List>
        </Box>
      </Popover>
    </Box>
  );
}
